import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, History, Percent, Wallet } from "lucide-react";
import { api, rp } from "@/lib/api";
import { Input } from "@/components/ui/input";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";
const CATS = [["external_rental", "Sewa Keluar (Pickup & Delivery)"],
  ["play_at_venue", "Main di Venue"]];
const CYCLES = [["monthly", "Bulanan"], ["biweekly", "Dua mingguan (tgl 1 & 16)"],
  ["weekly", "Mingguan (Senin)"], ["manual", "Manual — tanpa jadwal otomatis"]];

const ScheduleEditor = ({ hubId, editable }) => {
  const [s, setS] = useState(null);
  const [next, setNext] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/payouts", { params: { hub_id: hubId } })
      .then((r) => setS((r.data.hubs.find((h) => h.id === hubId) || {}).schedule
        || { cycle: "monthly", pay_day: 5, enabled: true }))
      .catch(() => setS({ cycle: "monthly", pay_day: 5, enabled: true }));
  }, [hubId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/hubs/${hubId}/payout-schedule`, s);
      setNext(r.data.next_period);
      toast.success(`Jadwal pembayaran disimpan (${r.data.label})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan jadwal");
    } finally { setSaving(false); }
  };

  if (!s) return null;
  return (
    <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="payout-schedule-editor">
      <div className="flex items-center gap-2">
        <CalendarClock size={14} className="text-lm-red" />
        <span className="text-sm font-semibold">Jadwal pembayaran komisi</span>
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" className="accent-lm-red" disabled={!editable}
            data-testid="payout-schedule-enabled" checked={!!s.enabled}
            onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
          Buat batch otomatis
        </label>
      </div>
      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-ink-soft">Siklus</span>
          <select className={`${sel} mt-1`} disabled={!editable} data-testid="payout-schedule-cycle"
            value={s.cycle} onChange={(e) => setS({ ...s, cycle: e.target.value })}>
            {CYCLES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        {s.cycle === "monthly" && (
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-soft">Tanggal bayar (1–28)</span>
            <Input type="number" min={1} max={28} className="mt-1 h-10" disabled={!editable}
              data-testid="payout-schedule-payday" value={s.pay_day}
              onChange={(e) => setS({ ...s, pay_day: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })} />
          </label>
        )}
        {editable && (
          <div className="flex items-end">
            <button data-testid="payout-schedule-save" onClick={save} disabled={saving}
              className="rounded-md border border-black/15 px-4 py-2.5 text-sm disabled:opacity-50">
              {saving ? "Menyimpan…" : "Simpan jadwal"}
            </button>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-ink-soft">
        Batch dibuat otomatis setiap 06:00 WIB saat periode jatuh tempo, isinya semua komisi yang
        sudah jadi hak dan belum dibayar.
        {next ? ` Periode berikutnya: ${next.start} s/d ${next.end}, bayar ${next.due}.` : ""}
      </p>
    </div>
  );
};

const RuleEditor = ({ cat, label, rule, preview, editable, onChange }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={`commission-card-${cat}`}>
    <div className="flex items-center gap-2">
      {cat === "play_at_venue" ? <Wallet size={14} className="text-lm-red" />
        : <Percent size={14} className="text-lm-red" />}
      <span className="text-sm font-semibold">{label}</span>
      <label className="ml-auto inline-flex items-center gap-1.5 text-xs cursor-pointer">
        <input type="checkbox" className="accent-lm-red" disabled={!editable}
          data-testid={`commission-enabled-${cat}`} checked={!!rule.enabled}
          onChange={(e) => onChange(cat, { enabled: e.target.checked })} />
        Aktif
      </label>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-3">
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-ink-soft">Jenis</span>
        <select className={`${sel} mt-1`} disabled={!editable} data-testid={`commission-mode-${cat}`}
          value={rule.mode} onChange={(e) => onChange(cat, { mode: e.target.value })}>
          <option value="percentage">Persen (%)</option>
          <option value="fixed_idr">Rupiah (tetap)</option>
        </select>
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-ink-soft">
          {rule.mode === "percentage" ? "Persen 0–100" : "Rupiah"}
        </span>
        <Input type="number" min={0} className="mt-1 h-10" disabled={!editable}
          data-testid={`commission-value-${cat}`} value={rule.value}
          onChange={(e) => onChange(cat, { value: Math.max(0, Number(e.target.value) || 0) })} />
      </label>
      {rule.mode === "fixed_idr" && (
        <label className="col-span-2 inline-flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" className="accent-lm-red" disabled={!editable}
            data-testid={`commission-perunit-${cat}`} checked={!!rule.per_unit}
            onChange={(e) => onChange(cat, { per_unit: e.target.checked })} />
          Hitung per meja (bukan per booking)
        </label>
      )}
      <label className="block col-span-2">
        <span className="text-[10px] uppercase tracking-widest text-ink-soft">Berlaku mulai (opsional)</span>
        <Input type="date" className="mt-1 h-10" disabled={!editable}
          data-testid={`commission-effective-${cat}`} value={(rule.effective_from || "").slice(0, 10)}
          onChange={(e) => onChange(cat, { effective_from: e.target.value })} />
      </label>
    </div>
    <div className="mt-3 text-xs text-ink-soft" data-testid={`commission-preview-${cat}`}>
      Contoh: {preview?.detail || "—"} → <b className="text-ink">{rp(preview?.amount || 0)}</b>
      <span className="ml-1">· versi {rule.version || 0}</span>
    </div>
  </div>
);

export const HubCommission = ({ hubId }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [base, setBase] = useState(500000);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/hubs/${hubId}/commissions`, { params: { base, quantity: qty } })
      .then((r) => { setData(r.data); setDraft(r.data.rules); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat komisi"));
  }, [hubId, base, qty]);
  useEffect(() => { load(); }, [load]);

  const onChange = (cat, patch) => setDraft((p) => ({ ...p, [cat]: { ...p[cat], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { reason };
      CATS.forEach(([c]) => {
        body[c] = { mode: draft[c].mode, value: Number(draft[c].value) || 0,
          enabled: !!draft[c].enabled, per_unit: !!draft[c].per_unit,
          effective_from: draft[c].effective_from || "" };
      });
      const r = await api.put(`/hubs/${hubId}/commissions`, body);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      toast.success("Aturan komisi disimpan");
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan komisi");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-sm text-ink-soft">Memuat…</div>;

  return (
    <div className="space-y-4" data-testid="hub-tab-commission-body">
      <p className="text-xs text-ink-soft">
        Dua aturan yang berdiri sendiri per Hub. Dasar hitung = pendapatan sewa/jasa yang benar-benar
        dibayar setelah diskon, termasuk jam tambahan, <b>tanpa</b> deposit dan tanpa biaya kurir
        customer. Booking yang sudah dibayar memakai aturan versi saat itu — mengubah angka di sini
        tidak pernah mengubah komisi lama.
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        {CATS.map(([cat, label]) => (
          <RuleEditor key={cat} cat={cat} label={label} rule={draft[cat] || {}}
            preview={data.preview?.[cat]} editable={data.editable} onChange={onChange} />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-ink-soft">Simulasi pendapatan</span>
          <Input type="number" className="mt-1 h-10 w-40" data-testid="commission-sim-base"
            value={base} onChange={(e) => setBase(Math.max(0, Number(e.target.value) || 0))} />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-ink-soft">Jumlah meja</span>
          <Input type="number" min={1} className="mt-1 h-10 w-24" data-testid="commission-sim-qty"
            value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} />
        </label>
        {data.editable && (
          <>
            <label className="block flex-1 min-w-[200px]">
              <span className="text-[10px] uppercase tracking-widest text-ink-soft">Alasan perubahan</span>
              <Input className="mt-1 h-10" data-testid="commission-reason" value={reason}
                onChange={(e) => setReason(e.target.value)} />
            </label>
            <button data-testid="commission-save" onClick={save} disabled={saving}
              className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
              {saving ? "Menyimpan…" : "Simpan Komisi"}
            </button>
          </>
        )}
      </div>

      <ScheduleEditor hubId={hubId} editable={data.editable} />

      <div>
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <History size={13} /> Riwayat perubahan
        </h3>        <div className="mt-2 overflow-x-auto bg-white border border-black/10 rounded-lg">
          <table className="w-full text-xs" data-testid="commission-history">
            <thead className="bg-cream-dark text-left text-[10px] uppercase tracking-widest text-ink-soft">
              <tr><th className="p-2.5">Waktu</th><th className="p-2.5">Kategori</th>
                <th className="p-2.5">Aturan</th><th className="p-2.5">Berlaku</th>
                <th className="p-2.5">Oleh</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {(data.history || []).map((h) => (
                <tr key={h.id} data-testid={`commission-history-${h.id}`}>
                  <td className="p-2.5">{String(h.at).slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2.5">{h.category === "play_at_venue" ? "Main di Venue" : "Sewa Keluar"}</td>
                  <td className="p-2.5">
                    {h.enabled ? "" : "(mati) "}
                    {h.mode === "percentage" ? `${h.value}%` : `${rp(h.value)}${h.per_unit ? " /meja" : ""}`}
                    <span className="text-ink-soft"> v{h.version}</span>
                  </td>
                  <td className="p-2.5">{h.effective_from ? h.effective_from.slice(0, 10) : "langsung"}</td>
                  <td className="p-2.5">{h.by}</td>
                </tr>
              ))}
              {(data.history || []).length === 0 && (
                <tr><td colSpan={5} className="p-3 text-ink-soft">Belum ada perubahan.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
